import React from 'react';
import { Link } from 'react-router-dom';

import { trackDownloadIndividualSamples } from '../../../../analytics';

import { Button } from '../../../../components/general';

import { ZIP_URL } from '../../../../constants';
import { VideoMode } from '../../../../types';
import { isEmpty, getPluralPhrase } from '../../../../utils';

import { UploadStage, UploadingClips } from '../../components/types';

// calculate total uploading progress based on each clip progress
const getProgress = (
  uploadingClips: UploadingClips,
  uploadingCount: number,
) => {
  let progress = 0;

  if (!isEmpty(uploadingClips)) {
    Object.keys(uploadingClips).forEach(
      (key) => (progress += uploadingClips[key].progress / uploadingCount),
    );
  }
  const uploadedCount = uploadingCount - Object.keys(uploadingClips).length;
  progress += (uploadedCount * 100) / uploadingCount;

  progress = Math.round(progress);

  return progress;
};

const getClipsText = (props: {
  stage: UploadStage;
  uploadingClips: UploadingClips;
  uploadingCount: number;
  clipsCount: number;
  orientation?: VideoMode;
  isPublic: boolean;
  isSample: boolean;
  handleUploadMore: (
    e: React.MouseEvent<HTMLElement>,
    position: string,
  ) => void;
}) => {
  const {
    stage,
    uploadingClips,
    uploadingCount,
    clipsCount,
    orientation,
    isPublic,
    isSample,
    handleUploadMore,
  } = props;

  const uploadInfoPoints = [
    'Your clips contain close-up footage of people',
    'Your clips are in MP4 or MOV format',
    !!orientation && `Your clips are in <b>${orientation}</b> orientation`,
    'Your clips are lightly edited (colour graded, stabilised)',
    'Your clips are unique from the highlight reel footage',
  ].filter(Boolean) as string[];

  const summaryInfoPoints = [
    'Move your cursor over a clip to preview it',
    "Move your cursor over a clip and click the 'X' button to delete it",
    "Click 'Upload More' to add new clips at any time",
    !!orientation &&
      `Note that your clips will be processed and optimised for <b>${orientation}</b>`,
  ].filter(Boolean) as string[];

  const subheading = (
    <>
      {isSample && (
        <>
          <b>
            Use the assets below by clicking on all the clips to load them into
            your project.
          </b>
          <br />
        </>
      )}
      Clips are close up videos of people, our AI will pick the best ones to use
      for your Highlight Reel.
      {!isSample && stage === 'to-upload' && !isPublic && (
        <div className="mt-3 italic">
          Not sure what to upload?{' '}
          <Link
            to={ZIP_URL.FIFA_SELECTS_ZIP}
            onClick={() => trackDownloadIndividualSamples('clips')}
          >
            Try our samples
          </Link>
        </div>
      )}
    </>
  );

  const progress = getProgress(uploadingClips, uploadingCount);

  // to upload or uploading view
  if (stage !== 'summary')
    return { subheading, progress, infoPoints: uploadInfoPoints };

  // clips are uploaded
  return {
    subheading: (
      <>
        {getPluralPhrase(clipsCount, 'clip')} currently uploaded.{' '}
        {!isSample && (
          <Button
            onClick={(e: React.MouseEvent<HTMLElement>) =>
              handleUploadMore(e, 'Top link')
            }
            variant="link"
          >
            Upload more
          </Button>
        )}
      </>
    ),
    progress,
    infoPoints: summaryInfoPoints,
  };
};

export default getClipsText;
